"use client";

import * as React from "react";
import { CircleCheck as CheckCircle2, CircleAlert as AlertCircle, PackageCheck, ShieldAlert, Loader as Loader2, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { EcommerceProductsProvider, useEcommerceProducts, type EcommerceProduct } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface ProductDetailsPanelProps {
  onProductLoaded: (product: EcommerceProduct | null) => void;
  quantity: number;
  onQuantityChange: (qty: number) => void;
  purchasePref: string;
  onPurchasePrefChange: (val: string) => void;
  formattedFullAmount: string;
  formattedDepositAmount: string;
}
function ProductDetailsConsumer({
  onProductLoaded,
  quantity,
  onQuantityChange,
  purchasePref,
  onPurchasePrefChange,
  formattedFullAmount,
  formattedDepositAmount
}: ProductDetailsPanelProps) {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  const product = products && products.length > 0 ? products[0] : null;
  React.useEffect(() => {
    onProductLoaded(product);
  }, [product, onProductLoaded]);
  if (loading) {
    return <div className="space-y-4 rounded-none border border-border bg-card p-6">
        <Skeleton className="h-6 w-1/3" />
        <div className="flex gap-4">
          <Skeleton className="h-28 w-28 shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-6 w-1/4" />
          </div>
        </div>
      </div>;
  }
  if (error || !product) {
    return <Alert variant="destructive" className="rounded-none">
        <AlertCircle className="size-4" />
        <AlertTitle>Product not found</AlertTitle>
        <AlertDescription>
          The specified machine or part could not be loaded. You can still submit your general requirements below.
        </AlertDescription>
      </Alert>;
  }
  const sku = product.customFields?.sku || String(product.id);
  const categoryName = product.categories?.[0]?.name;
  const inStock = product.is_in_stock !== false;
  const formattedDisplayPrice = product.formatted_discounted_price || product.formatted_price || "Inquire for pricing";
  return <div className="space-y-6 rounded-none border border-border bg-card p-6 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-4">
        <div>
          <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
            Selected equipment
          </span>
          <h2 className="text-xl font-bold tracking-tight text-card-foreground">
            {product.title}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {categoryName && <Badge variant="outline" className="rounded-none font-mono text-xs">
              {categoryName}
            </Badge>}
          <Badge variant={inStock ? "secondary" : "outline"} className="rounded-none font-mono text-xs uppercase">
            {inStock ? "Available to order" : "Backorder available"}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-4">
        <div className="sm:col-span-1">
          {product.featured_image_url ? <div className="aspect-square overflow-hidden border border-border bg-muted">
              <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover" loading="lazy" />
            </div> : <div className="flex aspect-square items-center justify-center border border-border bg-muted font-mono text-xs text-muted-foreground">
              No photo available
            </div>}
        </div>

        <div className="space-y-3 sm:col-span-3">
          <div className="grid grid-cols-2 gap-4 font-mono text-xs">
            <div>
              <span className="text-muted-foreground">Product reference:</span>
              <p className="font-semibold text-foreground">{sku}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Unit price:</span>
              <p className="font-semibold text-primary">{formattedDisplayPrice}</p>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="order-quantity" className="font-mono text-xs uppercase">
                Machine quantity
              </Label>
              <Input id="order-quantity" type="number" min="1" step="1" value={quantity} onChange={e => {
              const val = parseInt(e.target.value, 10);
              onQuantityChange(isNaN(val) || val < 1 ? 1 : val);
            }} className="rounded-none font-mono text-sm" />
            </div>

            <div className="space-y-1.5">
              <span className="font-mono text-xs uppercase text-muted-foreground">
                Equipment total
              </span>
              <div className="flex h-9 items-center border border-border bg-muted px-3 font-mono text-sm font-semibold text-foreground">
                {formattedFullAmount}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3 border-t border-border pt-4">
        <Label className="font-mono text-xs uppercase text-foreground">
          Purchase preference <span className="text-destructive">*</span>
        </Label>
        <RadioGroup value={purchasePref} onValueChange={onPurchasePrefChange} className="gap-3">
          <div className="flex items-start space-x-3 rounded-none border border-border bg-background p-3.5 hover:bg-accent/40">
            <RadioGroupItem value="full" id="pref-full" className="mt-0.5" />
            <Label htmlFor="pref-full" className="cursor-pointer font-normal leading-normal">
              <span className="font-semibold text-foreground">
                Pay in Full — {formattedFullAmount}
              </span>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Process balance immediately to release priority assembly and carrier queue.
              </p>
            </Label>
          </div>

          <div className="flex items-start space-x-3 rounded-none border border-border bg-background p-3.5 hover:bg-accent/40">
            <RadioGroupItem value="deposit" id="pref-deposit" className="mt-0.5" />
            <Label htmlFor="pref-deposit" className="cursor-pointer font-normal leading-normal">
              <span className="font-semibold text-foreground">
                Pay Deposit — {formattedDepositAmount}
              </span>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Choosing the deposit option reserves the order and begins preparation. Any remaining product balance must be paid before the scheduled shipping date.
              </p>
            </Label>
          </div>
        </RadioGroup>
      </div>
    </div>;
}
export default function OrderEnquiry() {
  const [productId, setProductId] = React.useState<string | null>(null);
  const [activeProduct, setActiveProduct] = React.useState<EcommerceProduct | null>(null);

  // Form submission & state
  const [formDef, setFormDef] = React.useState<any>(null);
  const [formLoading, setFormLoading] = React.useState<boolean>(true);
  const [formLoadError, setFormLoadError] = React.useState<string | null>(null);
  const [quantity, setQuantity] = React.useState<number>(1);
  const [purchasePref, setPurchasePref] = React.useState<string>("full");

  // User input fields
  const [fullName, setFullName] = React.useState<string>("");
  const [companyName, setCompanyName] = React.useState<string>("");
  const [email, setEmail] = React.useState<string>("");
  const [phone, setPhone] = React.useState<string>("");
  const [deliveryAddress, setDeliveryAddress] = React.useState<string>("");
  const [interestedIn, setInterestedIn] = React.useState<string>("");
  const [notes, setNotes] = React.useState<string>("");

  // Honeypot & bot security
  const [hpValue, setHpValue] = React.useState<string>("");
  const tokenFetchTime = React.useRef<number>(Date.now());

  // Submit states
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, {
    code?: string;
    message: string;
  }>>({});
  const [formError, setFormError] = React.useState<string | null>(null);
  const [confirmationHtml, setConfirmationHtml] = React.useState<string | null>(null);

  // Parse product_id from query params on mount
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      const searchParams = new URLSearchParams(window.location.search);
      const pid = searchParams.get("product_id");
      if (pid && !isNaN(Number(pid))) {
        setProductId(pid);
      }
    }
  }, []);

  // Fetch form definition
  const fetchFormDefinition = React.useCallback(async () => {
    setFormLoading(true);
    setFormLoadError(null);
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setFormLoadError("Form services are currently unreachable.");
      setFormLoading(false);
      return;
    }
    try {
      const def = await client.pluginRequest("wvc-forms/v1/forms/order-enquiry");
      setFormDef(def);
      tokenFetchTime.current = Date.now();
    } catch (err: any) {
      // Recovery check
      try {
        const list = await client.pluginRequest("wvc-forms/v1/forms");
        const found = list?.items?.find((f: any) => f.key === "order-enquiry") || (list?.items?.length === 1 ? list.items[0] : null) || list?.items?.find((f: any) => typeof f.name === "string" && f.name.toLowerCase().includes("order"));
        if (found?.key) {
          const fallbackDef = await client.pluginRequest(`wvc-forms/v1/forms/${found.key}`);
          setFormDef(fallbackDef);
          tokenFetchTime.current = Date.now();
        } else {
          setFormLoadError("Order enquiry form is currently offline.");
        }
      } catch {
        setFormLoadError("Unable to initialize order form.");
      }
    } finally {
      setFormLoading(false);
    }
  }, []);
  React.useEffect(() => {
    fetchFormDefinition();
  }, [fetchFormDefinition]);

  // Price calculations
  const unitPrice = React.useMemo(() => {
    if (!activeProduct) return 0;
    if (activeProduct.on_sale && typeof activeProduct.discounted_price === "number") {
      return activeProduct.discounted_price;
    }
    return typeof activeProduct.price === "number" ? activeProduct.price : 0;
  }, [activeProduct]);
  const fullAmount = unitPrice * quantity;
  const depositAmount = fullAmount * 0.20;
  const currencyPrefix = activeProduct?.currency_prefix ?? activeProduct?.currency_symbol ?? "$";
  const currencySuffix = activeProduct?.currency_suffix ?? "";
  const formatPriceVal = React.useCallback((amount: number) => {
    return `${currencyPrefix}${amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}${currencySuffix}`;
  }, [currencyPrefix, currencySuffix]);
  const formattedFullAmount = formatPriceVal(fullAmount);
  const formattedDepositAmount = formatPriceVal(depositAmount);
  const formattedUnitPrice = formatPriceVal(unitPrice);
  const productReferenceValue = activeProduct ? activeProduct.customFields?.sku || String(activeProduct.id) : "";

  // Handle Form Submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setFormError(null);
    const client = getWvcClient();
    if (!client?.pluginRequest || !formDef) {
      setFormError("Form processing is currently unavailable.");
      return;
    }

    // Check anti-spam minimum seconds
    const minSecs = formDef.spam?.min_seconds ?? 3;
    const elapsedSecs = (Date.now() - tokenFetchTime.current) / 1000;
    if (elapsedSecs < minSecs) {
      await new Promise(res => setTimeout(res, (minSecs - elapsedSecs + 0.5) * 1000));
    }
    setIsSubmitting(true);

    // Build values mapping to field IDs or keys expected by wvc-forms
    const fields = formDef.fields || [];
    const stripPunct = (s: string) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const getFieldId = (candidateKeys: string[], labelPatterns?: string[]) => {
      // 1. Candidate-key matching as the first attempt
      const keyMatch = fields.find((f: any) => candidateKeys.includes(f.key));
      if (keyMatch) return keyMatch.id;

      // 2. Fallback that locates the field by matching its label (case-insensitive, ignoring punctuation)
      if (labelPatterns && labelPatterns.length > 0) {
        const labelMatch = fields.find((f: any) => {
          if (!f.label) return false;
          const normFieldLabel = stripPunct(f.label);
          return labelPatterns.some(pattern => {
            const normPattern = stripPunct(pattern);
            return normFieldLabel === normPattern || normFieldLabel.includes(normPattern) || normPattern.includes(normFieldLabel);
          });
        });
        if (labelMatch) return labelMatch.id;
      }
      return null;
    };
    const values: Record<string, any> = {};

    // Standard field mapping helper
    const assignValue = (keys: string[], val: any, labelPatterns?: string[]) => {
      const id = getFieldId(keys, labelPatterns);
      if (id) {
        values[id] = val;
      }
    };

    // User inputs with candidate-key matching first, followed by resilient label matching fallback
    assignValue(["full_name", "name", "contact_name"], fullName, ["Full Name", "Contact Name"]);
    assignValue(["company_name", "company", "business_name"], companyName, ["Business or venue name", "Company Name"]);
    assignValue(["email", "email_address"], email, ["Email", "Email address"]);
    assignValue(["phone", "phone_number", "telephone"], phone, ["Phone", "Phone number"]);
    assignValue(["delivery_address", "shipping_address", "address", "destination_zip"], deliveryAddress, ["Delivery address", "Destination zip code", "delivery"]);
    assignValue(["notes", "comments", "additional_notes", "message"], notes, ["Special instructions", "notes"]);
    if (!productId) {
      assignValue(["interest", "interested_in", "requirements", "product_interest"], interestedIn, ["What are you interested in"]);
    }

    // Required/Hidden parameters as explicitly instructed
    assignValue(["selected_product", "product_name"], activeProduct?.title || "");
    assignValue(["product_reference", "sku", "item_reference"], productReferenceValue);
    assignValue(["unit_price"], formattedUnitPrice);
    assignValue(["full_amount", "total_amount"], formattedFullAmount);
    assignValue(["deposit_amount"], formattedDepositAmount);
    assignValue(["quantity", "qty"], String(quantity));
    assignValue(["purchase_preference", "payment_preference", "preference"], purchasePref === "deposit" ? `Pay Deposit — ${formattedDepositAmount}` : `Pay in Full — ${formattedFullAmount}`);
    const submitData: any = {
      values,
      token: formDef.spam?.token,
      meta: {
        source_url: typeof window !== "undefined" ? window.location.href : "",
        referrer: typeof document !== "undefined" ? document.referrer : ""
      }
    };
    if (formDef.spam?.honeypot?.field) {
      submitData.hp = {
        [formDef.spam.honeypot.field]: hpValue
      };
    }
    try {
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}/submit`, {
        method: "POST",
        data: submitData
      });
      if (res.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
          window.location.assign(res.confirmation.url);
        } else {
          setConfirmationHtml(res.confirmation?.message || "Thank You for Your Enquiry — We have received your product enquiry and will review the machine, selected payment option and delivery information you submitted. We will contact you with the next order steps and any additional information needed.");
          // Reset all input state variables so visitor can submit another enquiry
          setFullName("");
          setCompanyName("");
          setEmail("");
          setPhone("");
          setDeliveryAddress("");
          setInterestedIn("");
          setNotes("");
          setQuantity(1);
          setPurchasePref("full");
        }
      } else if (res.code === "frms_validation_failed") {
        setFieldErrors(res.errors || {});
        setFormError(res.message || "Please check the highlighted fields.");
      } else if (res.code === "frms_invalid_token") {
        // Token expired; reload form definition once
        await fetchFormDefinition();
        setFormError("Session expired. Please try submitting again.");
      } else {
        setFormError(res.message || "Submission failed. Please check your information.");
      }
    } catch (err: any) {
      setFormError(err?.message || "A network error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const wpQueryObj = React.useMemo(() => {
    if (!productId) return null;
    return new WP_Query({
      post_type: "product",
      p: Number(productId)
    });
  }, [productId]);
  return <section id="orderenquiry" data-nav="light" className="relative bg-background py-16 text-foreground md:py-24">
      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Header Title Area */}
        <div className="mb-10 text-left">
          <div className="inline-flex items-center gap-2 border border-border bg-muted px-3 py-1 font-mono text-xs uppercase tracking-wider text-muted-foreground">
            <PackageCheck className="size-3.5 text-primary" />
            <span>Commercial Equipment Procurement</span>
          </div>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">
            Submit order enquiry
          </h2>
          <p className="mt-2 max-w-2xl text-base text-muted-foreground">
            Direct sales order review for commercial mechanical bulls, replacements, and parts. Complete this procurement request to reserve allocation.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* Main Form Column */}
          <div className="space-y-8 lg:col-span-8">
            {/* Product Info Panel (If Product ID is present) */}
            {productId && wpQueryObj && <EcommerceProductsProvider wp_query={wpQueryObj}>
                <ProductDetailsConsumer onProductLoaded={setActiveProduct} quantity={quantity} onQuantityChange={setQuantity} purchasePref={purchasePref} onPurchasePrefChange={setPurchasePref} formattedFullAmount={formattedFullAmount} formattedDepositAmount={formattedDepositAmount} />
              </EcommerceProductsProvider>}

            {/* Confirmation Banner */}
            {confirmationHtml && <div className="border border-border bg-card p-8 shadow-sm">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="size-8 shrink-0 text-primary" />
                  <div className="space-y-4">
                    <h2 className="text-2xl font-bold tracking-tight text-foreground">
                      Order enquiry received
                    </h2>
                    <div className="prose text-sm leading-relaxed text-muted-foreground" dangerouslySetInnerHTML={{
                  __html: confirmationHtml
                }} />
                    <div className="border-t border-border pt-4">
                      <p className="font-mono text-xs text-muted-foreground">
                        Equipment sales desk: Monday – Friday, 8:00 AM – 5:00 PM CST.
                      </p>
                      <div className="mt-4 flex flex-wrap gap-3">
                        <Button asChild variant="outline" className="rounded-none font-mono text-xs">
                          <Link to="/ShopPage">Browse more equipment</Link>
                        </Button>
                        <Button asChild className="rounded-none font-mono text-xs">
                          <Link to="/HomePage">Return to home</Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>}

            {/* Form Loader / Error States */}
            {formLoading && <div className="space-y-4 border border-border bg-card p-6">
                <Skeleton className="h-6 w-1/4" />
                <div className="grid grid-cols-2 gap-4">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <Skeleton className="h-24 w-full" />
              </div>}

            {formLoadError && <Alert variant="destructive" className="rounded-none">
                <AlertCircle className="size-4" />
                <AlertTitle>Form unavailable</AlertTitle>
                <AlertDescription>{formLoadError}</AlertDescription>
              </Alert>}

            {/* Form Element */}
            {!formLoading && <form onSubmit={handleSubmit} className="space-y-6">
                {formError && <Alert variant="destructive" className="rounded-none">
                    <AlertCircle className="size-4" />
                    <AlertTitle>Submission issue</AlertTitle>
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>}

                {/* Honeypot Decoy */}
                {formDef?.spam?.honeypot?.field && <div style={{
              position: "absolute",
              left: "-9999px"
            }} aria-hidden="true">
                    <input type="text" name={formDef.spam.honeypot.field} tabIndex={-1} autoComplete="off" value={hpValue} onChange={e => setHpValue(e.target.value)} />
                  </div>}

                {/* If NO product ID provided: show the 'What are you interested in?' field */}
                {!productId && <div className="space-y-2 border border-border bg-card p-6 shadow-sm">
                    <Label htmlFor="interested-in" className="font-mono text-xs uppercase text-foreground">
                      What are you interested in? <span className="text-destructive">*</span>
                    </Label>
                    <Textarea id="interested-in" required rows={3} placeholder="Specify machine model, used inventory request, or required replacement parts..." value={interestedIn} onChange={e => setInterestedIn(e.target.value)} className="rounded-none font-sans" />
                    <p className="text-xs text-muted-foreground">
                      Please indicate the model or components you need specifications and freight quotes for.
                    </p>
                  </div>}

                {/* Customer and Contact Details */}
                <div className="space-y-6 border border-border bg-card p-6 shadow-sm">
                  <h2 className="border-b border-border pb-3 text-lg font-bold tracking-tight text-card-foreground">
                    Purchaser & delivery details
                  </h2>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="cust-name" className="font-mono text-xs uppercase">
                        Contact name <span className="text-destructive">*</span>
                      </Label>
                      <Input id="cust-name" required type="text" placeholder="Full name" value={fullName} onChange={e => setFullName(e.target.value)} className="rounded-none" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="cust-company" className="font-mono text-xs uppercase">
                        Business or venue name
                      </Label>
                      <Input id="cust-company" type="text" placeholder="Optional company name" value={companyName} onChange={e => setCompanyName(e.target.value)} className="rounded-none" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="cust-email" className="font-mono text-xs uppercase">
                        Email address <span className="text-destructive">*</span>
                      </Label>
                      <Input id="cust-email" required type="email" placeholder="procurement@example.com" value={email} onChange={e => setEmail(e.target.value)} className="rounded-none font-mono text-sm" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="cust-phone" className="font-mono text-xs uppercase">
                        Phone number <span className="text-destructive">*</span>
                      </Label>
                      <Input id="cust-phone" required type="tel" placeholder="(555) 000-0000" value={phone} onChange={e => setPhone(e.target.value)} className="rounded-none font-mono text-sm" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cust-address" className="font-mono text-xs uppercase">
                      Delivery address / Destination zip code <span className="text-destructive">*</span>
                    </Label>
                    <Input id="cust-address" required type="text" placeholder="Street, City, State, ZIP code (freight dock or liftgate)" value={deliveryAddress} onChange={e => setDeliveryAddress(e.target.value)} className="rounded-none" />
                    <p className="text-xs text-muted-foreground">
                      Used to calculate freight carrier transit and loading dock/liftgate requirements.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cust-notes" className="font-mono text-xs uppercase">
                      Special instructions or power setup notes
                    </Label>
                    <Textarea id="cust-notes" rows={3} placeholder="Specify single phase vs 3 phase electrical access, facility doors, or target delivery date..." value={notes} onChange={e => setNotes(e.target.value)} className="rounded-none" />
                  </div>
                </div>

                {/* Submit Action */}
                <div className="space-y-4">
                  <Button type="submit" disabled={isSubmitting} size="lg" className="w-full rounded-none font-mono text-sm font-bold uppercase tracking-wider shadow-md">
                    {isSubmitting ? <>
                        <Loader2 className="size-4 animate-spin" />
                        <span>Transmitting enquiry...</span>
                      </> : <span>SUBMIT ORDER ENQUIRY</span>}
                  </Button>
                  <p className="text-center font-mono text-xs text-muted-foreground">
                    No direct charge is processed upon submitting this form. Our sales engineers verify electrical requirements and shipping logistics before invoicing.
                  </p>
                </div>
              </form>}
          </div>

          {/* Sidebar Information Column */}
          <div className="space-y-6 lg:col-span-4">
            <div className="border border-border bg-card p-6 shadow-sm">
              <h2 className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                Order protocol
              </h2>
              <h3 className="mt-1 text-base font-bold text-card-foreground">
                How our order desk works
              </h3>
              <div className="mt-4 space-y-4 text-xs text-muted-foreground">
                <div className="flex gap-3">
                  <div className="flex size-5 shrink-0 items-center justify-center border border-border bg-muted font-mono font-bold text-foreground">
                    1
                  </div>
                  <p>
                    <strong className="text-foreground">Order Review:</strong> We verify inventory readiness, voltage configuration (110V/220V), and freight logistics.
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="flex size-5 shrink-0 items-center justify-center border border-border bg-muted font-mono font-bold text-foreground">
                    2
                  </div>
                  <p>
                    <strong className="text-foreground">Official Invoice:</strong> You receive an itemized commercial sales contract reflecting your selected payment path.
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="flex size-5 shrink-0 items-center justify-center border border-border bg-muted font-mono font-bold text-foreground">
                    3
                  </div>
                  <p>
                    <strong className="text-foreground">Dispatched Freight:</strong> Equipment ships via dedicated freight line with pre-delivery tracking and dispatch coordination.
                  </p>
                </div>
              </div>
            </div>

            <div className="border border-border bg-muted/50 p-6">
              <div className="flex items-start gap-3">
                <ShieldAlert className="size-5 shrink-0 text-primary" />
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-foreground">Commercial equipment sales only</h4>
                  <p className="text-xs text-muted-foreground">
                    We supply venues, training centers, and corporate facilities. We do not provide rental units or event staffing.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3 border border-border bg-card p-6 shadow-sm">
              <h4 className="text-sm font-bold text-card-foreground">Need direct phone assistance?</h4>
              <p className="text-xs text-muted-foreground">
                Speak directly with an equipment specialist regarding lead times, custom colors, or international shipping tariffs.
              </p>
              <div className="pt-2">
                <Button asChild variant="outline" className="w-full rounded-none font-mono text-xs">
                  <Link to="/ContactPage">Contact technical desk</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}